import { RJSFSchema, UiSchema } from '@rjsf/utils'
import validator from '@rjsf/validator-ajv8'
import { merge, omit, pick } from 'lodash'
import { useState } from 'react'

import Form, { extractUiSchema } from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/components/Form'
import proxyAction from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/globalActions/proxy.json'
import useDatasource from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/hooks/useDatasource'
import useSearchParams from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/hooks/useSearchParams'
import { Datasource } from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/interfaces'
import { requiredActionKeys } from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/utilities/constants'

type MetadataProps = {
  onChange: (key: string) => void
}

export function Metadata({ onChange }: MetadataProps) {
  const [datasource, setDatasource] = useDatasource(true)
  const [formErrors, setFormErrors] = useState<{ key: string }>()
  const datasourceActions: typeof datasource.actions = { _proxy: proxyAction, ...datasource.actions }

  const [searchParams, setSearchParams] = useSearchParams()

  const activeActionKey = searchParams.get('action')!

  const activeAction = datasourceActions[activeActionKey]

  const handleChange = (data: { key: string } & Pick<Datasource['actions'][string], 'name' | 'description'>) => {
    if (!activeActionKey || !data.key) return
    if (activeActionKey !== data.key && datasourceActions[data.key]) {
      setFormErrors({
        key: 'Action with this key already exists.'
      })
      return
    } else {
      setFormErrors(undefined)
    }
    setDatasource(datasource => ({
      ...datasource,
      actions: {
        ...omit(datasource.actions, [activeActionKey]),
        [data.key]: {
          ...(data.key === '_proxy' ? proxyAction : {}),
          ...datasource.actions[activeActionKey],
          ...pick(data, ['name', 'description'])
        }
      }
    }))
    setSearchParams(new URLSearchParams({ action: data.key }))
    onChange(data.key)
  }

  const schema: RJSFSchema = {
    type: 'object',
    properties: {
      key: {
        type: 'string',
        title: 'Key',
        description:
          'The key is used to identify the action in the workflow. DO NOT change this value if the action is already in use.'
      },
      name: {
        type: 'string',
        title: 'Name'
      },
      description: {
        type: 'string',
        title: 'Description'
      }
    },
    required: ['key']
  }

  const uiSchema: UiSchema<any, typeof schema> = merge(extractUiSchema(schema), {
    'ui:options': {
      submitButtonOptions: {
        norender: true
      }
    },
    key: {
      'ui:options': {
        disabled: requiredActionKeys.has(activeActionKey),
        help: requiredActionKeys.has(activeActionKey) && 'Required action keys cannot be changed'
      }
    }
  })

  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      <p className="mt-1 h-[5px] text-sm text-red-500">{formErrors?.key}</p>
      <Form
        className="flex flex-1 flex-col"
        validator={validator}
        schema={schema}
        uiSchema={uiSchema}
        showErrorList={false}
        formData={{ key: activeActionKey, ...pick(activeAction, ['name', 'description']) }}
        onChange={event => handleChange(event.formData)}
      />
    </div>
  )
}
